/**
 * テンプレート選択コンポーネント
 *
 * 構造化面接テンプレートを選択するためのUI
 * 科学的根拠: Schmidt & Hunter (1998), Taylor & Small (2002)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border } from '../../../../utils/darkModeAdapter';
import { templateService } from '../../services/audioAPI';
import { designTokens } from '../../../../theme/designTokens';
import TemplateCreationDialog from './TemplateCreationDialog';

const theme = designTokens.featureThemes.interview_support;

/**
 * テンプレート選択コンポーネント
 */
export default function TemplateSelector({
  onSelectTemplate,
  selectedTemplateId = null,
  onEditTemplate = null,
  showActions = true
}) {
  const muiTheme = useTheme();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobCategoryFilter, setJobCategoryFilter] = useState('');
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [templateToDuplicate, setTemplateToDuplicate] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [creationDialogOpen, setCreationDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDetailTemplate, setSelectedDetailTemplate] = useState(null);

  // テンプレート一覧を取得
  useEffect(() => {
    loadTemplates();
  }, [jobCategoryFilter]);

  const loadTemplates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await templateService.getTemplates(jobCategoryFilter || null);
      // APIはリストを直接返す
      setTemplates(Array.isArray(data) ? data : (data.templates || []));
    } catch (err) {
      setError('テンプレートの読み込みに失敗しました');
      console.error('Failed to load templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // テンプレートを複製
  const handleDuplicate = async () => {
    if (!templateToDuplicate || !newTemplateName.trim()) return;

    try {
      const duplicated = await templateService.duplicateTemplate(
        templateToDuplicate.id,
        newTemplateName.trim()
      );
      setTemplates([...templates, duplicated]);
      setDuplicateDialogOpen(false);
      setTemplateToDuplicate(null);
      setNewTemplateName('');
    } catch (err) {
      setError('テンプレートの複製に失敗しました');
      console.error('Failed to duplicate template:', err);
    }
  };

  // テンプレートを削除
  const handleDelete = async (templateId) => {
    if (!window.confirm('このテンプレートを削除しますか？')) return;

    try {
      await templateService.deleteTemplate(templateId);
      setTemplates(templates.filter(t => t.id !== templateId));
      if (selectedTemplateId === templateId) {
        onSelectTemplate(null);
      }
    } catch (err) {
      setError('テンプレートの削除に失敗しました');
      console.error('Failed to delete template:', err);
    }
  };

  // 職種カテゴリの選択肢
  const jobCategories = ['', 'エンジニア', 'ビジネス', 'クリエイティブ', 'バックオフィス'];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon sx={{ color: theme.primary }} />
          構造化面接テンプレート
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>職種カテゴリ</InputLabel>
            <Select
              value={jobCategoryFilter}
              label="職種カテゴリ"
              onChange={(e) => setJobCategoryFilter(e.target.value)}
            >
              <MenuItem value="">すべて</MenuItem>
              {jobCategories.filter(c => c).map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {showActions && onEditTemplate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreationDialogOpen(true)}
              size="small"
              sx={{ backgroundColor: theme.primary, '&:hover': { backgroundColor: theme.dark } }}
            >
              新規作成
            </Button>
          )}
        </Box>
      </Box>

      {/* 科学的根拠の説明 */}
      <Alert
        icon={<ScienceIcon sx={{ color: theme.primary }} />}
        sx={{
          mb: 2,
          backgroundColor: `${theme.primary}08`,
          border: `1px solid ${theme.primary}30`,
          '& .MuiAlert-message': { color: textColors(muiTheme).primary }
        }}
      >
        <Typography variant="body2">
          <strong>科学的根拠:</strong> 構造化面接は非構造化面接と比較して予測妥当性が高く（ρ=.42 vs .20）、
          BARS（行動アンカー評価尺度）を用いることで評価者間信頼性が向上します。
          <br />
          <em style={{ color: textColors(muiTheme).muted }}>参考: Schmidt & Hunter (1998), Taylor & Small (2002), Sackett et al. (2022)</em>
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* テンプレートカード一覧 */}
      <Grid container spacing={2}>
        {templates.map((template) => (
          <Grid item xs={12} md={6} key={template.id}>
            <Card
              sx={{
                height: '100%',
                border: selectedTemplateId === template.id ? `2px solid ${theme.primary}` : `1px solid ${border(muiTheme).default}`,
                backgroundColor: selectedTemplateId === template.id ? `${theme.primary}08` : bg(muiTheme).card,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 3,
                  borderColor: theme.light
                }
              }}
              onClick={() => onSelectTemplate(template)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" component="div">
                    {template.template_name}
                    {selectedTemplateId === template.id && (
                      <CheckCircleIcon sx={{ ml: 1, verticalAlign: 'middle', color: theme.primary }} />
                    )}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {template.description || '説明なし'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip
                    label={template.job_category}
                    size="small"
                    sx={{ borderColor: theme.primary, color: theme.primary }}
                    variant="outlined"
                  />
                  {template.experience_level && (
                    <Chip
                      label={template.experience_level}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>

                {/* コンピテンシー一覧 */}
                <Typography variant="caption" color="text.secondary">
                  評価コンピテンシー:
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {template.competencies?.slice(0, 4).map((comp, idx) => (
                    <Chip
                      key={idx}
                      label={comp.name}
                      size="small"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ))}
                  {template.competencies?.length > 4 && (
                    <Chip
                      label={`+${template.competencies.length - 4}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </CardContent>

              {showActions && (
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <Tooltip title="詳細を見る">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDetailTemplate(template);
                        setDetailDialogOpen(true);
                      }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="複製">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTemplateToDuplicate(template);
                        setNewTemplateName(`${template.template_name} (コピー)`);
                        setDuplicateDialogOpen(true);
                      }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                  {!template.is_default && onEditTemplate && (
                    <>
                      <Tooltip title="編集">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditTemplate(template);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(template.id);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </CardActions>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {templates.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            テンプレートが見つかりません
          </Typography>
        </Paper>
      )}

      {/* 複製ダイアログ */}
      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)}>
        <DialogTitle>テンプレートを複製</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            「{templateToDuplicate?.template_name}」を複製します。
          </Typography>
          <TextField
            label="新しいテンプレート名"
            fullWidth
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)}>キャンセル</Button>
          <Button
            onClick={handleDuplicate}
            variant="contained"
            disabled={!newTemplateName.trim()}
          >
            複製
          </Button>
        </DialogActions>
      </Dialog>

      {/* テンプレート詳細ダイアログ */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: `${theme.primary}10`, borderBottom: `1px solid ${border(muiTheme).default}` }}>
          {selectedDetailTemplate?.template_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedDetailTemplate && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedDetailTemplate.description || '説明なし'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <Chip label={selectedDetailTemplate.job_category} size="small" sx={{ borderColor: theme.primary, color: theme.primary }} variant="outlined" />
                {selectedDetailTemplate.experience_level && (
                  <Chip label={selectedDetailTemplate.experience_level} size="small" variant="outlined" />
                )}
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                評価コンピテンシー
              </Typography>
              <Box sx={{ mb: 3 }}>
                {selectedDetailTemplate.competencies?.map((comp, idx) => {
                  const competencyKey = comp.id || comp.name;
                  const barsData = selectedDetailTemplate.bars_scales?.[competencyKey];
                  // recommended_questionsが配列形式かDict形式かを判定
                  const questions = Array.isArray(selectedDetailTemplate.recommended_questions)
                    ? selectedDetailTemplate.recommended_questions.filter(q => q.competency === competencyKey)
                    : (selectedDetailTemplate.recommended_questions?.[competencyKey] || []);

                  return (
                    <Box key={idx} sx={{ mb: 2, p: 2, backgroundColor: bg(muiTheme).subtle, borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {comp.label || comp.name}
                        </Typography>
                        <Chip label={`${(comp.weight * 100).toFixed(0)}%`} size="small" />
                      </Box>
                      {comp.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {comp.description}
                        </Typography>
                      )}

                      {/* BARS評価基準 */}
                      {barsData && (
                        <Box sx={{ mt: 1.5, pl: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.primary }}>
                            BARS評価基準
                          </Typography>
                          {[5, 4, 3, 2, 1].map(score => {
                            const anchor = barsData[score];
                            const anchorText = typeof anchor === 'string' ? anchor : anchor?.anchor;
                            if (!anchorText) return null;
                            return (
                              <Box key={score} sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={score}
                                  size="small"
                                  sx={{
                                    minWidth: 24,
                                    height: 20,
                                    fontSize: '0.7rem',
                                    backgroundColor: score >= 4 ? '#4caf50' : score >= 3 ? '#ff9800' : '#f44336',
                                    color: '#fff'
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {anchorText}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      {/* 推奨質問 */}
                      {questions.length > 0 && (
                        <Box sx={{ mt: 1.5, pl: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.primary }}>
                            推奨質問
                          </Typography>
                          {questions.map((q, qIdx) => (
                            <Typography key={qIdx} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              • {q.question}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Alert severity="info" icon={<ScienceIcon />} sx={{ mt: 2 }}>
                <Typography variant="caption">
                  構造化面接の予測妥当性: ρ=.42（Schmidt & Hunter, 1998）
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>閉じる</Button>
          {selectedDetailTemplate && !selectedDetailTemplate.is_default && onEditTemplate && (
            <Button
              variant="contained"
              onClick={() => {
                setDetailDialogOpen(false);
                onEditTemplate(selectedDetailTemplate);
              }}
              sx={{ backgroundColor: theme.primary }}
            >
              編集
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* テンプレート作成方法選択ダイアログ */}
      <TemplateCreationDialog
        open={creationDialogOpen}
        onClose={() => setCreationDialogOpen(false)}
        onCreateFromTemplate={(templateDetail) => {
          // 既存テンプレートをベースに編集画面を開く
          if (onEditTemplate) {
            onEditTemplate({
              ...templateDetail,
              id: null, // 新規作成として扱う
              template_name: `${templateDetail.template_name}（カスタム）`,
              is_default: false,
              _baseTemplateId: templateDetail.id // 元テンプレートの参照
            });
          }
        }}
        onCreateNew={() => {
          // 空の編集画面を開く
          if (onEditTemplate) {
            onEditTemplate(null);
          }
        }}
      />
    </Box>
  );
}
